import React from "react";

interface ModeRendererProps {
  brand: string;
  today: string;
  Copy: string;
}

export function VasarelyVinciMode({ brand }: ModeRendererProps) {
  const width = 794;
  const height = 1123;

  const bgGradient = (
    <defs>
      <linearGradient id="vinciGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style={{ stopColor: "#8B0000", stopOpacity: 1 }} />
        <stop offset="50%" style={{ stopColor: "#C41E3A", stopOpacity: 1 }} />
        <stop offset="100%" style={{ stopColor: "#2C3E50", stopOpacity: 1 }} />
      </linearGradient>
    </defs>
  );

  // Bridge arcs - infrastructure curves
  const bridgeArcs: JSX.Element[] = [];
  for (let arc = 0; arc < 8; arc++) {
    const y = -400 + arc * 100;
    const points: string[] = [];
    
    for (let p = 0; p <= 40; p++) {
      const t = p / 40;
      const x = -360 + t * 720;
      const yOffset = -Math.sin(t * Math.PI) * 80;
      points.push(`${x},${y + yOffset}`);
    }
    
    bridgeArcs.push(
      <polyline
        key={`bridge-${arc}`}
        points={points.join(" ")}
        fill="none"
        stroke="#FFFFFF"
        strokeWidth={2.5}
        opacity={0.3}
      />
    );
  }

  // Cable lines - suspension bridge cables
  const cableLines: JSX.Element[] = [];
  for (let cable = 0; cable < 32; cable++) {
    const x = -360 + cable * 22.5;
    
    cableLines.push(
      <line
        key={`cable-${cable}`}
        x1={x}
        y1={-480}
        x2={x}
        y2={480}
        stroke="#C41E3A"
        strokeWidth={cable % 4 === 0 ? 2 : 1}
        opacity={cable % 4 === 0 ? 0.3 : 0.15}
        strokeDasharray="12,6"
      />
    );
  }

  // Transport grids - connection networks
  const transportGrids: JSX.Element[] = [];
  const gridLayers = 16;
  
  for (let layer = 0; layer < gridLayers; layer++) {
    const radius = 50 + layer * 25;
    
    transportGrids.push(
      <circle
        key={`transport-${layer}`}
        cx={0}
        cy={0}
        r={radius}
        fill="none"
        stroke="#FFFFFF"
        strokeWidth={layer % 4 === 0 ? 2.5 : 1.5}
        opacity={0.3 - layer * 0.014}
      />
    );
  }

  // Connection nodes - trust system waypoints
  const connectionNodes: JSX.Element[] = [];
  const nodeAngles = [0, Math.PI / 4, Math.PI / 2, 3 * Math.PI / 4, Math.PI, 5 * Math.PI / 4, 3 * Math.PI / 2, 7 * Math.PI / 4];
  const nodeRadius = 240;
  
  nodeAngles.forEach((angle, i) => {
    const nx = Math.cos(angle) * nodeRadius;
    const ny = Math.sin(angle) * nodeRadius;
    
    connectionNodes.push(
      <g key={`connection-${i}`}>
        <circle
          cx={nx}
          cy={ny}
          r={40}
          fill="none"
          stroke="#C41E3A"
          strokeWidth={3}
          opacity={0.5}
        />
        <circle
          cx={nx}
          cy={ny}
          r={25}
          fill="none"
          stroke="#FFFFFF"
          strokeWidth={2}
          opacity={0.4}
        />
        <circle
          cx={nx}
          cy={ny}
          r={12}
          fill="#C41E3A"
          opacity={0.6}
        />
      </g>
    );
  });

  // Network connections
  const networkConnections: JSX.Element[] = [];
  nodeAngles.forEach((angle1, i) => {
    const angle2 = nodeAngles[(i + 1) % nodeAngles.length];
    const x1 = Math.cos(angle1) * nodeRadius;
    const y1 = Math.sin(angle1) * nodeRadius;
    const x2 = Math.cos(angle2) * nodeRadius;
    const y2 = Math.sin(angle2) * nodeRadius;
    
    networkConnections.push(
      <line
        key={`net-${i}`}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="#FFFFFF"
        strokeWidth={2}
        opacity={0.2}
        strokeDasharray="10,5"
      />
    );
  });

  return (
    <svg width={width} height={height} xmlns="http://www.w3.org/2000/svg">
      {bgGradient}
      <rect width={width} height={height} fill="url(#vinciGradient)" />
      <g transform={`translate(${width / 2}, ${height / 2})`}>
        {cableLines}
        {bridgeArcs}
        {transportGrids}
        {networkConnections}
        {connectionNodes}
        <circle cx={0} cy={0} r={55} fill="none" stroke="#FFFFFF" strokeWidth={3} opacity={0.6} />
        <circle cx={0} cy={0} r={38} fill="none" stroke="#C41E3A" strokeWidth={2.5} opacity={0.5} />
        <circle cx={0} cy={0} r={22} fill="#C41E3A" opacity={0.4} />
      </g>
    </svg>
  );
}
